import type { Team } from "@redotech/redo-team-common/team";
import type {
  IntakeAdditionalStepConfig,
  IntakeAnswers,
  IntakeAuthorizationStepConfig,
  IntakeInfoStepConfig,
  IntakeQuestion,
} from "../../registry/types";
import { isAnswered } from "./intake-validation";

export const SETUP_STEP_PARAM = "setupStep";

export type IntakeStep =
  | { id: "welcome"; type: "welcome" }
  | { additionalStepIndex: number; id: string; type: "additional" }
  | { id: string; questionIndex: number; type: "question" }
  | { id: "finalInfo"; type: "finalInfo" }
  | { authorizationStepId: string; id: string; type: "authorization" };

export function buildIntakeSteps({
  additionalSteps = [],
  authorizationStep,
  finalInfoStep,
  isLocalDevelopment = process.env.NODE_ENV === "development",
  questions,
  team,
}: {
  additionalSteps?: IntakeAdditionalStepConfig[];
  authorizationStep?: IntakeAuthorizationStepConfig;
  finalInfoStep: IntakeInfoStepConfig | null;
  isLocalDevelopment?: boolean;
  questions: IntakeQuestion[];
  team: Team | null | undefined;
}): IntakeStep[] {
  const steps: IntakeStep[] = [{ id: "welcome", type: "welcome" }];
  for (const [additionalStepIndex, step] of additionalSteps.entries()) {
    steps.push({ additionalStepIndex, id: step.id, type: "additional" });
  }
  for (const [questionIndex, question] of questions.entries()) {
    steps.push({
      id: `question:${question.id}`,
      questionIndex,
      type: "question",
    });
  }
  if (finalInfoStep) {
    steps.push({ id: "finalInfo", type: "finalInfo" });
  }
  if (
    authorizationStep &&
    (!team ||
      !authorizationStep.completionPredicate(team) ||
      (isLocalDevelopment &&
        authorizationStep.showWhenCompleteInDevelopment === true))
  ) {
    steps.push({
      authorizationStepId: authorizationStep.id,
      id: authorizationStep.id,
      type: "authorization",
    });
  }
  return steps;
}

export function resolveInitialIntakeStepId({
  authorizationStepId,
  requestedStepId,
  steps,
}: {
  authorizationStepId?: string;
  requestedStepId: string | null;
  steps: IntakeStep[];
}): string {
  if (steps.length === 0) {
    return "welcome";
  }
  if (requestedStepId) {
    const requestedStep = steps.find((step) => step.id === requestedStepId);
    if (requestedStep) {
      return requestedStep.id;
    }
    if (requestedStepId === authorizationStepId) {
      return steps[steps.length - 1]!.id;
    }
  }
  return steps[0]!.id;
}

export function canUseRequestedIntakeStep({
  additionalSteps = [],
  answers,
  authorizationStepId,
  questions,
  requestedStepId,
  steps,
}: {
  additionalSteps?: IntakeAdditionalStepConfig[];
  answers: IntakeAnswers;
  authorizationStepId?: string;
  questions: IntakeQuestion[];
  requestedStepId: string | null;
  steps: IntakeStep[];
}): boolean {
  if (!requestedStepId) {
    return true;
  }

  let requestedIndex = steps.findIndex((step) => step.id === requestedStepId);
  if (requestedIndex < 0 && requestedStepId === authorizationStepId) {
    requestedIndex = steps.length - 1;
  }
  if (requestedIndex <= 0) {
    return true;
  }

  return steps.slice(0, requestedIndex).every((step) => {
    if (step.type === "additional") {
      const additionalStep = additionalSteps[step.additionalStepIndex];
      return additionalStep?.isComplete?.(answers) === true;
    }
    if (step.type !== "question") {
      return true;
    }
    const question = questions[step.questionIndex];
    return !!question && isAnswered(question, answers[question.id]);
  });
}

export function getNextIntakeStepId(
  steps: IntakeStep[],
  currentStepId: string,
): string | null {
  const currentIndex = steps.findIndex((step) => step.id === currentStepId);
  if (currentIndex < 0) {
    return steps[0]?.id ?? null;
  }
  return steps[currentIndex + 1]?.id ?? null;
}

export function getIntakeProgressPercentage(
  steps: IntakeStep[],
  currentStepId: string | undefined,
): number {
  if (!currentStepId || steps.length === 0) {
    return 0;
  }
  const currentIndex = steps.findIndex((step) => step.id === currentStepId);
  if (currentIndex < 0) {
    return 0;
  }
  return Math.round(((currentIndex + 1) / steps.length) * 100);
}

export function isIntakeContinueDisabled({
  allAnswered,
  authorizationPending,
  currentAdditionalStepComplete,
  currentAnswered,
  currentStep,
  submitting,
}: {
  allAnswered: boolean;
  authorizationPending: boolean;
  currentAdditionalStepComplete?: boolean;
  currentAnswered: boolean;
  currentStep: IntakeStep | undefined;
  submitting: boolean;
}): boolean {
  if (submitting || authorizationPending || !currentStep) {
    return true;
  }
  if (currentStep.type === "question") {
    return !currentAnswered;
  }
  if (currentStep.type === "additional") {
    return currentAdditionalStepComplete !== true;
  }
  if (currentStep.type === "finalInfo") {
    return !allAnswered;
  }
  return false;
}
